const BASE_URL = process.env.REACT_APP_API_URL;

const headers = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
});

function onAuthFailure() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('page');
  window.dispatchEvent(new Event('auth:unauthorized'));
}

async function req(method, path, body) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: headers(),
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      onAuthFailure();
    }
    const err = await res.json().catch(() => ({ message: 'Network error' }));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Auth ──────────────────────────────────────────────────
export const authApi = {
  //login: (email, password) => req('POST', '/auth/login', { email, password }),
  //NEW — form data to /account with username/password
login: (username, password) => {
  const form = new FormData();
  form.append('username', username);
  form.append('password', password);
  return fetch(`${BASE_URL}/account/login`, { method: 'POST',credentials: 'include', body: form })
    .then(r => r.json());
},
  forgotPassword: (username) => {
    const form = new FormData();
    form.append('username', username);
    return fetch(`${BASE_URL}/Account/forgotpassword`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    }).then(async r => {
      const res = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(res.message || `HTTP ${r.status}`);
      return res;
    });
  },
  clientAuth: (otp, expireTime, clientOTP) => {
    return fetch(`${BASE_URL}/Account/ClientAuth`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      body: JSON.stringify({ OTP: otp, expireTime, clientOTP }),
    }).then(async r => {
      const res = await r.json().catch(() => ({}));
      if (!r.ok) {
        const err = new Error(res.message || `HTTP ${r.status}`);
        err.page = res.page || '';
        throw err;
      }
      return res;
    });
  },
  resendClientOTP: () => {
    return fetch(`${BASE_URL}/Account/resendclientotp`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
    }).then(async r => {
      const res = await r.json().catch(() => ({}));
      if (!r.ok) {
        const err = new Error(res.message || `HTTP ${r.status}`);
        err.page = res.page || '';
        throw err;
      }
      return res;
    });
  },
  changePassword: (oldPassword, confirmPassword) => {
    return fetch(`${BASE_URL}/Account/changepassword`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      body: JSON.stringify({ OldPassword: oldPassword, ConfirmPassword: confirmPassword }),
    }).then(async r => {
      const res = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(res.message || `HTTP ${r.status}`);
      return res;
    });
  },
  changePasswordForcefully: (oldPassword, confirmPassword) => {
    return fetch(`${BASE_URL}/account/changepasswordforcefully`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      body: JSON.stringify({ OldPassword: oldPassword, ConfirmPassword: confirmPassword }),
    }).then(async r => {
      const res = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(res.message || `HTTP ${r.status}`);
      return res;
    });
  },
  logout: () => fetch(`${BASE_URL}/account/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
  }).catch(() => {}),
  loginOtp: (mobile) => req('POST', '/auth/login-otp', { mobile }),
  verifyOtp: (mobile, otp) => req('POST', '/auth/verify-otp', { mobile, otp }),
  // 4-digit numeric CAPTCHA for the Register Company form.
  getCaptcha: () => {
    return fetch(`${BASE_URL}/account/captcha`, { method: 'GET', credentials: 'include' })
      .then(async r => {
        const res = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(res.message || `HTTP ${r.status}`);
        return res;
      });
  },
  // Pre-login "middle page" — Company Name, Client Name, Phone + Email + captcha answer.
  // Stages the signup and emails a verification link (no credentials yet — see verifyEmail).
  registerCompany: (companyName, clientName, phone, email, captchaId, captchaAnswer) => {
    const form = new FormData();
    form.append('companyName', companyName);
    form.append('clientName', clientName);
    form.append('phone', phone);
    form.append('email', email);
    form.append('captchaId', captchaId);
    form.append('captchaAnswer', captchaAnswer);
    return fetch(`${BASE_URL}/account/registercompany`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    }).then(async r => {
      const res = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(res.message || `HTTP ${r.status}`);
      return res;
    });
  },
  // Called after the user clicks the verification link emailed by registerCompany.
  // On success this is what actually creates the account and emails login credentials.
  verifyEmail: (token) => {
    return fetch(`${BASE_URL}/account/verifyemail`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }).then(async r => {
      const res = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(res.message || `HTTP ${r.status}`);
      return res;
    });
  },
  // Basic account info for the logged-in user — company name, client name, email, phone.
  getBasicInfo: () => req('GET', '/account/basicinfo'),
};

// ── Dashboard ─────────────────────────────────────────────
export const dashboardApi = {
  getSummary: () => req('GET', '/dashboard/summary'),
  getAttentionItems: () => req('GET', '/dashboard/attention-items'),
  getRecentOrders: () => req('GET', '/dashboard/recent-orders'),
  // ADD this to dashboardApi:
  getClientDashboard: () => req('GET', '/client/dashboard'),
};

// ── Orders ────────────────────────────────────────────────
export const ordersApi = {
  list: (params) => {
    const qs = new URLSearchParams(params).toString();
    return req('GET', `/orders?${qs}`);
  },
  myOrders: (body = {}) => req('POST', '/order/myorders', body),
  addCommunication: (formData) => {
    return fetch(`${BASE_URL}/order/insuffuploaded`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
      const res = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(res.message || `HTTP ${r.status}`);
      return res;
    });
  },
  exportToExcel: async (params) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''))
    ).toString();
    const res = await fetch(`${BASE_URL}/order/exporttoexcelorders?${qs}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return blob;
  },
  orderCandidate: () => req('GET', '/order/ordercandidate'),
  getPackagesList: (projectId) => req('GET', `/order/getpackageslist?ProjectId=${projectId}`),
  getPackagesDetails: (packageId) => req('GET', `/order/getpackagesdetails?PackageId=${packageId}`),
  get: (id) => req('GET', `/orders/${id}`),
  createInvite: (data) => req('POST', '/orders/invite', data),
  createDirect: (data) => req('POST', '/orders/direct', data),
  bulkUploadValidate: (formData) => {
    return fetch(`${BASE_URL}/orders/bulk/validate`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      body: formData,
    }).then(r => r.json());
  },
  bulkPlace: (data) => req('POST', '/orders/bulk/place', data),
  orderReportPost: (formData) => {
    return fetch(`${BASE_URL}/Order/OrderReport`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (!r.ok) {
        if (r.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        const err = await r.json().catch(() => ({ message: 'Network error' }));
        throw new Error(err.message || `HTTP ${r.status}`);
      }
      if (r.status === 204) return null;
      return r.json();
    });
  },
  orderCandidatePost: (formData) => {
    return fetch(`${BASE_URL}/Order/ordercandidate`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (!r.ok) {
        if (r.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        const err = await r.json().catch(() => ({ message: 'Network error' }));
        throw new Error(err.message || `HTTP ${r.status}`);
      }
      if (r.status === 204) return null;
      return r.json();
    });
  },
  // OrderCandidateChecks returns JSON { message } via Ok()/BadRequest() — HTTP status is the
  // success/failure signal. `message` may still carry a legacy "true|~|"/"false|~|" prefix baked
  // in on the backend, so strip that before showing it to the user.
  addChecksToProfile: (formData) => {
    return fetch(`${BASE_URL}/Order/OrderCandidateChecks`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
      const res = await r.json().catch(() => ({}));
      const raw = res?.message || '';
      const message = raw.includes('|~|') ? raw.split('|~|').slice(1).join('|~|') : raw;
      if (!r.ok) throw new Error(message || `HTTP ${r.status}`);
      return { message: message || 'Checks added successfully.' };
    });
  },
  bulkOrderReport: (formData) => {
    return fetch(`${BASE_URL}/Order/bulkorderreport`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (!r.ok) {
        if (r.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        const err = await r.json().catch(() => ({ message: 'Network error' }));
        throw new Error(err.message || `HTTP ${r.status}`);
      }
      if (r.status === 204) return null;
      return r.json();
    });
  },
  bulkCandidateOrder: (formData) => {
    return fetch(`${BASE_URL}/Order/bulkcandidateorder`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (!r.ok) {
        if (r.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        const err = await r.json().catch(() => ({ message: 'Network error' }));
        throw new Error(err.message || `HTTP ${r.status}`);
      }
      if (r.status === 204) return null;
      return r.json();
    });
  },
  sentReminder: (profileId) => {
    const qs = new URLSearchParams({ ProfileId: profileId }).toString();
    return fetch(`${BASE_URL}/Order/sentreminder?${qs}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    }).then(async r => {
      const data = await r.json().catch(() => ({}));
      return { ok: r.ok, data };
    });
  },
  getCandidateDetailsByID: (profileId, profileStatus) => {
    const qs = new URLSearchParams({ PROFILE_ID: profileId || '', PROFILE_STATUS: profileStatus || '' }).toString();
    return req('GET', `/Order/getcandidatedetailsbyid?${qs}`);
  },
  requestChargesApprovalOrReject: (approvalType, chargesType, caseNumber, profileNumber, profileId, profileName) => {
    const qs = new URLSearchParams({
      ApprovalType:  approvalType  || '',
      ChargesType:   chargesType   || '',
      CaseNumber:    caseNumber    || '',
      profileNumber: profileNumber || '',
      ProfileId:     profileId     || '',
      ProfileName:   profileName   || '',
    }).toString();
    return req('POST', `/Order/RequestChargesApprovalOrReject?${qs}`);
  },
  downloadBGCReport: (profileNumber) => {
    const encoded = (() => {
      try { return btoa(unescape(encodeURIComponent(profileNumber))); }
      catch { return encodeURIComponent(profileNumber); }
    })();
    return fetch(`${BASE_URL}/Order/DownloadBGCReport?ProfileNumber=${encoded}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    }).then(async r => {
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get('content-disposition') || '';
      const match = cd.match(/filename[^;=\n]*=(['"]?)([^'";\n]*)\1/);
      const filename = match ? match[2] : `BGC-${profileNumber}.pdf`;
      return { blob, filename };
    });
  },
  downloadAttachment: (attId, casecode, casetype) => {
    const qs = new URLSearchParams({
      AttID:    String(attId    || ''),
      casecode: casecode || '',
      casetype: casetype || '',
    }).toString();
    return fetch(`${BASE_URL}/Order/downloadattachment?${qs}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    }).then(async r => {
      if (!r.ok || r.status === 204) return null;
      const blob = await r.blob();
      if (!blob || blob.size === 0) return null;
      const disposition = r.headers.get('content-disposition') || '';
      const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]*)\1/);
      const filename = match ? match[2] : `attachment_${attId}`;
      return { blob, filename };
    });
  },
  getBulkOrderPrice: (clientId, projectId, packageId, excelFile) => {
    const fd = new FormData();
    fd.append('ClientId', clientId || '');
    fd.append('ProjectId', projectId || '');
    fd.append('PackageId', packageId || '');
    if (excelFile) fd.append('ExcelFile', excelFile);
    return fetch(`${BASE_URL}/Order/getbulkorderprice`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: fd,
    }).then(async r => {
      if (!r.ok) {
        if (r.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        const err = await r.json().catch(() => ({ message: 'Network error' }));
        throw new Error(err.message || `HTTP ${r.status}`);
      }
      if (r.status === 204) return null;
      return r.json();
    });
  },
  downloadSampleExcel: (clientId, projectId, pkgId) => {
    const qs = new URLSearchParams({ ClientId: clientId || '', ProjectId: projectId || '', PkgId: pkgId || '' }).toString();
    return fetch(`${BASE_URL}/Order/downloadsampleexcel?${qs}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    }).then(async r => {
      if (!r.ok) {
        if (r.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        throw new Error(`HTTP ${r.status}`);
      }
      return r.blob();
    });
  },
  approveCharge: (id, chargeId) => req('POST', `/orders/${id}/charges/${chargeId}/approve`),
  rejectCharge: (id, chargeId) => req('POST', `/orders/${id}/charges/${chargeId}/reject`),
  downloadReport: (id) => `${BASE_URL}/orders/${id}/report`,
  exportCsv: (params) => {
    const qs = new URLSearchParams(params).toString();
    return `${BASE_URL}/orders/export?${qs}&token=${localStorage.getItem('token') || ''}`;
  },
};

// ── Wallet ────────────────────────────────────────────────
export const walletApi = {
  getWalletDetails: () => req('GET', '/wallet/WalletDetails'),
  getBalance: () => req('GET', '/wallet/balance'),
  getLedger: (params) => {
    const qs = new URLSearchParams(params).toString();
    return req('GET', `/wallet/ledger?${qs}`);
  },
  initiateTopup: (data) => req('POST', '/wallet/topup', data),
  createRazorpayOrder: (data) => req('POST', '/wallet/CreateRazorpayOrder', data),
  proceedToRazorpay: (data) => req('POST', '/wallet/ProceedToRazorpay', data),
  initiateCCAvenue: (data) => req('POST', '/wallet/InitiateCCAvenue', data),
  getInvoices: (params) => {
    const qs = new URLSearchParams(params).toString();
    return req('GET', `/wallet/invoices?${qs}`);
  },
  getWalletTransactions: () => req('GET', '/wallet/WalletTransactions'),
  getInvoiceHistory: () => req('GET', '/wallet/InvoiceHistory'),
  getCandidateWiseBreakdown: (fromDate, toDate) => {
    const qs = new URLSearchParams({ fromDate: fromDate || '', toDate: toDate || '' }).toString();
    return req('GET', `/wallet/CandidateWiseBreakdown?${qs}`);
  },
  downloadInvoice: (id) => `${BASE_URL}/wallet/invoices/${id}/download`,
  downloadAllInvoices: (fy) => `${BASE_URL}/wallet/invoices/download-all?fy=${fy}`,
};

// ── Reports ───────────────────────────────────────────────
export const reportsApi = {
  getMonthlyUsage: (month, year) => req('GET', `/reports/monthly?month=${month}&year=${year}`),
  exportExcel: (month, year) => `${BASE_URL}/reports/monthly/export-excel?month=${month}&year=${year}`,
  exportPdf: (month, year) => `${BASE_URL}/reports/monthly/export-pdf?month=${month}&year=${year}`,
};

// ── Onboarding ────────────────────────────────────────────
export const onboardingApi = {
  getCountryList: () => req('GET', '/OnBoarding/getcountrylist'),
  getStateList: (id) => req('GET', `/OnBoarding/getstatelist?id=${encodeURIComponent(id)}`),
  // NOT YET IMPLEMENTED on the backend — placeholder for the future GSTIN lookup API.
  // Expected response shape once wired up: { legalName, tradeName, city, state, country, status }
  verifyGstin: (gstin) => req('GET', `/OnBoarding/verifygstin?gstin=${encodeURIComponent(gstin)}`),
  // Register-your-company Step 2 (Agreement page): à la carte check list + pricing.
  // Response items are lowercase-keyed: { text, value, price, currency, mainchecktype }.
  // ClientId is read server-side from the JWT, not passed by the client.
  getAlacarteDetails: () => req('GET', '/OnBoarding/getalacartedetails'),
  // DigiSign "scroll to unlock" agreement preview — the backend merges the same à la carte
  // agreement docx template used by downloadAgreementAlacarte and returns it as HTML, so the
  // preview always reflects the current template with no frontend changes needed if it changes.
  getAgreementHtmlAlacarte: () => {
    const qs = new URLSearchParams({ casetype: 'Contract', fileName: 'PREPAID' }).toString();
    return req('GET', `/OnBoarding/getagreementhtmlalacarte?${qs}`);
  },
  // Saves the rendered signature (PNG data URL) and the typed signer name so they can be
  // embedded in the agreement PDF at ##SIGN## / ##SIGNNAME## respectively.
  saveSignature: (signatureImage, signName) => req('POST', '/OnBoarding/savesignature', { SignatureImage: signatureImage, SignName: signName }),
  // "Sign & Activate Account" click: stores a copy of the signed agreement and approves onboarding.
  // ClientId is read server-side from the JWT, not passed by the client.
  signAndActivateAgreement: () => req('POST', '/OnBoarding/signandactivateagreement', {}),
  getDetails: (liclass) => {
    const params = {};
    if (liclass)  params.liclass  = liclass;
    const qs = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
    return req('GET', `/OnBoarding/addclientdetails${qs}`);
  },
  saveContactDetails: (body) => {
    return fetch(`${BASE_URL}/OnBoarding/saveclientcontactdetails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
      },
      credentials: 'include',
      body: JSON.stringify(body),
    }).then(async r => {
      if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
      const data = await r.json().catch(() => ({}));
      return { ok: r.ok, data };
    });
  },
  // Single call for Register-your-company Step 1: company details + contacts + Bill To/Ship To.
  registerCompanyStep1: (body) => {
    return fetch(`${BASE_URL}/OnBoarding/registercompanystep1`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
      },
      credentials: 'include',
      body: JSON.stringify(body),
    }).then(async r => {
      if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
      const data = await r.json().catch(() => ({}));
      return { ok: r.ok, data };
    });
  },
  // Register-your-company Step 1: GST/PAN/TAN document uploads, sent as a separate
  // multipart request right after registerCompanyStep1's JSON body succeeds (that endpoint
  // has no file fields). formData field names must be GSTCopy/PANCopy/TANCopy — they're
  // bound directly to the controller action's IFormFile parameters.
  uploadClientRegistrationDocuments: (formData) => {
    return fetch(`${BASE_URL}/OnBoarding/uploadclientregistrationdocuments`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
      const data = await r.json().catch(() => ({}));
      return { ok: r.ok, data };
    });
  },
  submitBillingInfo: (qs, formData) => {
    const qStr = new URLSearchParams({
      ClientId:      String(qs.ClientId      || ''),
      BillingAction: qs.BillingAction        || 'Add',
    }).toString();
    return fetch(`${BASE_URL}/OnBoarding/submitclientbillinginformation?${qStr}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
      const data = await r.json().catch(() => ({}));
      return { ok: r.ok, data };
    });
  },
  // ClientId is read server-side from the JWT, not passed by the client.
  downloadAgreement: async () => {
    const qs = new URLSearchParams({ casetype: 'Contract', fileName: 'PREPAID' }).toString();
    const r = await fetch(`${BASE_URL}/OnBoarding/downloadagreement?${qs}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    });
    if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const filename = 'PrePaid Client Agreement.pdf';
    return { blob, filename };
  },
  // Same as downloadAgreement, but for new (à la carte only) clients — used on the
  // Register-your-company Step 2 (Agreement) page, which has no package pricing.
  downloadAgreementAlacarte: async () => {
    const qs = new URLSearchParams({ casetype: 'Contract', fileName: 'PREPAID' }).toString();
    const r = await fetch(`${BASE_URL}/OnBoarding/downloadagreementalacarte?${qs}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    });
    if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const filename = 'PrePaid Client Agreement.pdf';
    return { blob, filename };
  },
  downloadAttachment: (fileName, casetype = 'Contract') => {
    const qs = new URLSearchParams({ casetype, fileName: fileName || '' }).toString();
    return fetch(`${BASE_URL}/OnBoarding/DownloadAttachment?${qs}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    }).then(async r => {
      if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const disp = r.headers.get('content-disposition') || '';
      const mch  = disp.match(/filename[^;=\n]*=(['"]?)([^'";\n]*)\1/);
      return { blob, filename: mch ? mch[2] : fileName };
    });
  },
  submitContractInfo: (params, formData) => {
    const qs = new URLSearchParams({
      ClientId:              String(params.ClientId              || ''),
      ContractAction:        params.ContractAction               || '',
      hdncontractcopy:       params.hdncontractcopy              || '',
      hdncontractcopyoriginal: params.hdncontractcopyoriginal    || '',
      hdncontractcopyext:    params.hdncontractcopyext           || '',
    }).toString();
    return fetch(`${BASE_URL}/OnBoarding/submitclientcontractinformation?${qs}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
      body: formData,
    }).then(async r => {
      if (r.status === 401 || r.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
      const data = await r.json().catch(() => ({}));
      return { ok: r.ok, data };
    });
  },
};

// ── SuperAdmin ────────────────────────────────────────────
export const superAdminApi = {
  getClients: () => req('GET', '/client/getclientlist'),
  getClientListView: (fromDate, toDate) => {
    const qs = new URLSearchParams({ fromDate: fromDate || '', toDate: toDate || '' }).toString();
    return req('GET', `/client/getclientlistview?${qs}`);
  },
  exportClientListExcel: async (fromDate, toDate) => {
    const qs = new URLSearchParams({ fromDate: fromDate || '', toDate: toDate || '' }).toString();
    const res = await fetch(`${BASE_URL}/client/exportclientlistexcel?${qs}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return blob;
  },
  getClientRegistrations: (fromDate, toDate) => {
    const qs = new URLSearchParams({ fromDate: fromDate || '', toDate: toDate || '' }).toString();
    return req('GET', `/client/getclientregistrations?${qs}`);
  },
  exportClientRegistrationsExcel: async (search, status, fromDate, toDate) => {
    const qs = new URLSearchParams({ search: search || '', status: status || '', fromDate: fromDate || '', toDate: toDate || '' }).toString();
    const res = await fetch(`${BASE_URL}/client/exportclientregistrationsexcel?${qs}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },
  deleteClientRegistrations: (clientRegistrationIds) => req('POST', '/client/deleteclientregistrations', clientRegistrationIds),
  getWalletDetails: (clientId) => req('GET', `/wallet/WalletDetails?ClientId=${clientId}`),
  getWalletTransactions: (clientId) => req('GET', `/wallet/WalletTransactions?ClientId=${clientId}`),
  getInvoiceHistory: (clientId) => req('GET', `/wallet/InvoiceHistory?ClientId=${clientId}`),
  getCandidateWiseBreakdown: (fromDate, toDate, clientId) => {
    const qs = new URLSearchParams({ fromDate: fromDate || '', toDate: toDate || '', ClientId: clientId || '' }).toString();
    return req('GET', `/wallet/CandidateWiseBreakdown?${qs}`);
  },
  // fromDate/toDate must be dd-MMM-yyyy. Returns { summary, recentcreditsadded, creditsspent }.
  getWalletDashboard: (fromDate, toDate, topN = 6) => {
    const qs = new URLSearchParams({ fromDate: fromDate || '', toDate: toDate || '', topN: String(topN) }).toString();
    return req('GET', `/client/walletdashboard?${qs}`);
  },
  // fromDate/toDate must be dd-MMM-yyyy. Returns { data: { items, totalCount, page, pageSize } }.
  getPaymentTransactions: (fromDate, toDate, page = 1, pageSize = 20) => {
    const qs = new URLSearchParams({ fromDate: fromDate || '', toDate: toDate || '', page: String(page), pageSize: String(pageSize) }).toString();
    return req('GET', `/wallet/PaymentTransactions?${qs}`);
  },
};

// ── Settings ──────────────────────────────────────────────
export const settingsApi = {
  getProfile: () => req('GET', '/settings/profile'),
  updateProfile: (data) => req('PUT', '/settings/profile', data),
  getUsers: () => req('GET', '/settings/users'),
  inviteUser: (data) => req('POST', '/settings/users', data),
  updateUser: (id, data) => req('PUT', `/settings/users/${id}`, data),
  removeUser: (id) => req('DELETE', `/settings/users/${id}`),
  getNotifications: () => req('GET', '/settings/notifications'),
  updateNotifications: (data) => req('PUT', '/settings/notifications', data),
  getClientSettings: () => req('GET', '/client/getclientsettings'),
  updateClientSettings: async (data) => {
    const res = await fetch(`${BASE_URL}/client/updateclientsettings`, {
      method: 'POST',
      headers: headers(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (res.status === 401 || res.status === 403) { onAuthFailure(); throw new Error('Unauthorized'); }
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, message: json.message || 'Something went wrong. Please try again.' };
  },
  getPackages: () => req('GET', '/settings/packages'),
};
